import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Store as StoreIcon, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Admin-only: manage the multi-store list for a specific user.
// Embedded inside EditUserDialog. Fetches fresh data on mount.
export const StoresManager = ({ userId, userEmail }) => {
  const [stores, setStores] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/users/${userId}/stores`);
      setStores(res.data.stores || []);
      setActiveId(res.data.active_store_id || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load stores");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Store name is required");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/api/admin/users/${userId}/stores`, {
        name,
        address: newAddress.trim(),
      });
      toast.success(`Added store "${name}"`);
      setNewName("");
      setNewAddress("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add store");
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditName(s.name || "");
    setEditAddress(s.address || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditAddress("");
  };

  const handleSaveEdit = async (storeId) => {
    const name = editName.trim();
    if (!name) {
      toast.error("Store name cannot be empty");
      return;
    }
    try {
      await axios.put(`${API}/api/admin/users/${userId}/stores/${storeId}`, {
        name,
        address: editAddress.trim(),
      });
      toast.success("Store updated");
      cancelEdit();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update store");
    }
  };

  const handleDelete = async (s, idx) => {
    if (idx === 0) {
      toast.error("Cannot remove the primary store — rename it instead.");
      return;
    }
    if (stores.length <= 1) {
      toast.error("User must have at least one store");
      return;
    }
    if (!window.confirm(`Remove "${s.name}"? Data saved under this store will be hidden but not deleted.`)) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}/stores/${s.id}`);
      toast.success(`Removed store "${s.name}"`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove store");
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-stores-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-mono text-sm font-bold text-primary">Stores ({stores.length})</h3>
          <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
            Each store has its own separate Daily Entries, LVM, Coupons, and Store Paperwork.
          </p>
        </div>
      </div>

      {/* Existing stores */}
      <div className="space-y-2">
        {loading && stores.length === 0 && (
          <p className="text-xs font-mono text-muted-foreground">Loading…</p>
        )}
        {stores.map((s, idx) => {
          const isActive = s.id === activeId;
          const isEditing = editingId === s.id;
          const isPrimary = idx === 0;
          return (
            <div
              key={s.id}
              className={`rounded-lg border p-2.5 flex items-start gap-2 ${
                isActive ? "border-primary/50 bg-primary/5" : "border-border/40 bg-background/40"
              }`}
              data-testid={`admin-store-row-${s.id}`}
            >
              <div className="pt-0.5">
                {isActive ? (
                  <div className="w-6 h-6 rounded-md bg-primary/20 flex items-center justify-center" title="User's active store">
                    <Check className="w-3.5 h-3.5 text-primary" />
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded-md bg-muted/40 flex items-center justify-center">
                    <StoreIcon className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0 space-y-1.5">
                {isEditing ? (
                  <>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Store name"
                      className="h-8 text-sm font-mono"
                      data-testid={`admin-store-edit-name-${s.id}`}
                    />
                    <Input
                      value={editAddress}
                      onChange={(e) => setEditAddress(e.target.value)}
                      placeholder="Address"
                      className="h-8 text-sm font-mono"
                      data-testid={`admin-store-edit-address-${s.id}`}
                    />
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold text-card-foreground truncate">
                        {s.name || "(unnamed)"}
                      </span>
                      {isPrimary && (
                        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-500">
                          Primary
                        </span>
                      )}
                      {isActive && (
                        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/30 text-primary">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground font-mono leading-tight">
                      {s.address || <span className="italic opacity-60">No address</span>}
                    </p>
                  </>
                )}
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                {isEditing ? (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleSaveEdit(s.id)}
                      className="h-7 px-2 text-xs text-green-600 hover:text-green-700"
                      data-testid={`admin-store-save-${s.id}`}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={cancelEdit}
                      className="h-7 px-2 text-xs"
                      data-testid={`admin-store-cancel-${s.id}`}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(s)}
                      className="h-7 px-2 text-xs text-blue-500"
                      data-testid={`admin-store-edit-${s.id}`}
                    >
                      Rename
                    </Button>
                    {!isPrimary && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(s, idx)}
                        className="h-7 px-2 text-xs text-red-500"
                        data-testid={`admin-store-delete-${s.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add store form */}
      <div className="rounded-lg border border-dashed border-border/60 p-3 space-y-2 bg-muted/10">
        <p className="text-xs font-mono font-semibold text-card-foreground">Add a new store for {userEmail}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Name *</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Store #2 - Elm St"
              className="h-8 text-sm font-mono"
              data-testid="admin-store-new-name"
            />
          </div>
          <div className="space-y-1">
            <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Address</Label>
            <Input
              value={newAddress}
              onChange={(e) => setNewAddress(e.target.value)}
              placeholder="123 Elm St, City, ST"
              className="h-8 text-sm font-mono"
              data-testid="admin-store-new-address"
            />
          </div>
        </div>
        <Button
          onClick={handleAdd}
          disabled={adding || !newName.trim()}
          size="sm"
          className="btn-primary gap-1.5 h-8"
          data-testid="admin-store-add-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          {adding ? "Adding…" : "Add Store"}
        </Button>
      </div>
    </div>
  );
};

export default StoresManager;
